/**
 * Unit models, batch 3 (ADR-0075): the industrial and modern army, steel ships and
 * aircraft. Aircraft are modelled in flight a little above the tile.
 */

import * as T from 'three';
import { COL, kit, mat, person, shadeOf, type Kit } from './parts.js';
import { mounted } from './units-ancient.js';
import { hull } from './units-medieval.js';
import type { ModelBuilder } from './units.js';

const GREY = 0x8a949c;
const GREY_D = 0x6b747c;
const KHAKI = 0x8b8358;
const RED_HULL = 0x8a3a30;

/** A steel hull along x with a sharp bow, a red waterline and a stripe in the owner's colour. */
function steelHull(k: Kit, civ: number, length: number, beam: number, deck: number, colour = GREY): void {
  const h = length / 2;
  k.shape(
    [[-h + 0.6, 0.4], [h - 3.0, 0.4], [h + 0.8, deck + 0.7], [h - 0.4, deck + 0.7], [h - 1.6, deck], [-h, deck], [-h - 0.3, deck - 0.6], [-h, 1.4]],
    beam,
    colour,
    0,
    0,
    -beam / 2,
  );
  for (const z of [beam / 2 + 0.02, -beam / 2 - 0.02]) {
    k.box(length - 2.4, 0.7, 0.04, -0.4, 0.9, z, RED_HULL);
    k.box(length - 3.6, 0.35, 0.05, -0.6, deck - 0.5, z, civ);
  }
  k.box(length - 2.0, 0.2, beam - 0.3, -0.4, deck + 0.1, 0, 0xb4ab98);
}

/** A gun turret: a low drum with barrels pointing forward (+x, or turned by `turn`). */
function turret(g: T.Object3D, k: Kit, x: number, y: number, size: number, barrels: number, turn = 0): void {
  const t = new T.Group();
  t.position.set(x, y, 0);
  t.rotation.y = turn;
  g.add(t);
  const tk = kit(t);
  tk.bar([0, 0, 0], [0, size * 0.55, 0], size * 0.75, size * 0.85, GREY_D, 10);
  tk.box(size * 1.4, size * 0.5, size * 1.2, size * 0.1, size * 0.5, 0, GREY);
  for (let i = 0; i < barrels; i++) {
    const z = (i - (barrels - 1) / 2) * size * 0.32;
    tk.bar([size * 0.6, size * 0.55, z], [size * 2.6, size * 0.6, z], size * 0.09, size * 0.1, GREY_D, 6);
  }
  void k;
}

function funnel(k: Kit, civ: number, x: number, deck: number, height: number, r: number): void {
  k.bar([x, deck, 0], [x - 0.3, deck + height, 0], r * 0.9, r, GREY, 10);
  k.bar([x - 0.25, deck + height * 0.7, 0], [x - 0.3, deck + height * 0.85, 0], r * 0.92, r * 0.92, civ, 10);
  k.bar([x - 0.3, deck + height, 0], [x - 0.32, deck + height + 0.25, 0], r * 0.9, r * 0.9, COL.BLACK, 10);
}

/** A propeller fighter or bomber fuselage along x. */
function aircraft(g: T.Group, civ: number, span: number, length: number, engines: number, swept: boolean, height = 5.0): Kit {
  const k = kit(g);
  const body = swept ? GREY : 0x9aa48a;
  k.bar([-length / 2, height + 0.4, 0], [length / 2 - 1.2, height, 0], 0.5, 1.0, body, 10).rotation.x = 0;
  k.cone([length / 2 - 1.2, height, 0], [length / 2 + (swept ? 1.6 : 0.2), height, 0], 1.0, body, 10);
  if (swept) {
    for (const sgn of [1, -1]) {
      const w = k.shape([[length * 0.2, 0], [-length * 0.3, span / 2], [-length * 0.38, span / 2], [-length * 0.32, 0]], 0.3, body, 0, height - 0.15, 0);
      w.rotation.x = (sgn * Math.PI) / 2;
      w.position.y = height;
    }
    for (const z of [0.7, -0.7]) k.shape([[0, 0], [-1.2, 3.4], [-2.6, 3.4], [-2.8, 0]], 0.15, body, -length / 2 + 2.6, height + 0.3, z - 0.07);
  } else {
    k.box(length * 0.22, 0.3, span, length * 0.08, height - 0.2, 0, body);
    k.box(2.0, 0.2, span * 0.38, -length / 2 + 1.0, height + 0.3, 0, body);
    k.shape([[0, 0], [-1.6, 2.8], [-2.6, 2.8], [-2.4, 0]], 0.18, body, -length / 2 + 2.2, height + 0.3, -0.09);
    for (const z of [span / 2 - 0.6, -span / 2 + 0.6]) k.box(1.2, 0.32, 0.06, length * 0.08, height - 0.2, z, civ);
  }
  // Canopy and the owner's roundels on wings and fuselage.
  k.ball(0.75, length / 2 - 3.0, height + 0.85, 0, 0x9cc3d3, 1, 2.0, 0.8, 0.9);
  for (const z of [span * 0.32, -span * 0.32]) {
    k.disc(swept ? 0.9 : 1.1, 12, COL.WHITE, length * (swept ? -0.1 : 0.08), height + 0.12, z, 0).rotation.x = -Math.PI / 2;
    k.disc(swept ? 0.55 : 0.65, 12, civ, length * (swept ? -0.1 : 0.08), height + 0.14, z, 0).rotation.x = -Math.PI / 2;
  }
  for (const z of [1.0, -1.0]) k.disc(0.6, 12, civ, -length * 0.2, height + 0.2, z, z > 0 ? 0 : Math.PI);
  if (engines > 0) {
    const offsets = engines === 1 ? [0] : [-span * 0.36, -span * 0.18, span * 0.18, span * 0.36].slice(0, engines);
    for (const z of offsets) {
      const x = engines === 1 ? length / 2 + 0.2 : length * 0.22;
      if (engines > 1) k.bar([x - 2.4, height - 0.4, z], [x, height - 0.4, z], 0.5, 0.6, body, 8);
      const prop = new T.Mesh(new T.CircleGeometry(engines === 1 ? 1.8 : 1.3, 16), new T.MeshBasicMaterial({ color: 0x2b2f33, transparent: true, opacity: 0.25, side: T.DoubleSide }));
      prop.rotation.y = Math.PI / 2;
      prop.position.set(x + 0.15, engines === 1 ? height : height - 0.4, z);
      g.add(prop);
      k.box(0.12, engines === 1 ? 3.4 : 2.4, 0.3, x + 0.1, engines === 1 ? height : height - 0.4, z, COL.BLACK, 0.6);
    }
  }
  // A shadow on the ground beneath.
  const shadow = new T.Mesh(new T.CircleGeometry(span * 0.32, 18), new T.MeshBasicMaterial({ color: 0x1d2730, transparent: true, opacity: 0.18 }));
  shadow.rotation.x = -Math.PI / 2;
  shadow.scale.set(length / span, 1, 1);
  shadow.position.y = 0.02;
  g.add(shadow);
  return k;
}

export const MODERN: Record<string, ModelBuilder> = {
  rifleman(g, civ) {
    const k = person(g, civ, {
      helmet: 'kepi',
      coat: true,
      legs: 0x3e4a5a,
      boots: COL.BLACK,
      belt: COL.BLACK,
      lHand: [1.8, 10.4, 0.8],
      rHand: [0.6, 8.0, -1.0],
    });
    // Rifle at the port with a fixed bayonet, a pack and blanket roll.
    k.bar([-0.6, 6.2, -0.8], [3.2, 14.6, 0.6], 0.15, 0.15, COL.WOOD, 5);
    k.bar([2.2, 12.4, 0.25], [3.2, 14.6, 0.6], 0.1, 0.1, COL.IRON, 5);
    k.cone([3.2, 14.6, 0.6], [3.9, 16.6, 0.85], 0.1, COL.STEEL, 4);
    k.box(1.4, 2.6, 2.6, -1.8, 10.2, 0, COL.BLACK);
    k.bar([-1.8, 11.9, -1.5], [-1.8, 11.9, 1.5], 0.5, 0.5, 0x8a3a30, 8);
    for (let y = 8.4; y <= 11.0; y += 0.65) k.box(0.12, 0.2, 0.2, 1.3, y, 0.45, COL.BRONZE);
  },

  infantry(g, civ) {
    const k = person(g, civ, {
      helmet: 'kettle',
      tunic: KHAKI,
      legs: KHAKI,
      boots: COL.BLACK,
      belt: 0x6a6440,
      lHand: [1.6, 10.6, 1.0],
      rHand: [1.0, 12.4, -1.4],
      rElbow: [0.6, 10.2, -2.4],
    });
    // Arm band and collar patches in the owner's colour, webbing, a slung rifle and a pack.
    k.bar([0.15, 9.6, 1.5], [0.25, 10.2, 1.6], 0.5, 0.5, civ, 8);
    for (const z of [0.5, -0.5]) k.box(0.1, 0.4, 0.4, 1.0, 11.6, z, civ);
    k.box(0.12, 4.6, 0.35, 1.28, 9.5, 0.5, 0x6a6440, 0.5);
    for (const z of [-0.8, 0.8]) k.box(0.5, 0.6, 0.5, 1.2, 7.9, z, 0x6a6440);
    k.bar([1.0, 12.2, -1.4], [-1.6, 17.6, -2.0], 0.15, 0.15, COL.WOOD, 5);
    k.bar([-0.8, 15.8, -1.8], [-1.6, 17.6, -2.0], 0.1, 0.1, COL.IRON, 5);
    k.box(1.4, 2.8, 2.4, -1.8, 10.0, 0, 0x6a6440);
    k.box(1.2, 0.8, 0.6, -0.4, 6.8, 1.5, 0x5a5a40);
  },

  cavalry(g, civ) {
    const { rider: k } = mounted(g, civ, { saddleCloth: true, coat: 0x5a4030 }, {
      helmet: 'kepi',
      legs: 0x3e4a5a,
      boots: COL.BLACK,
      lHand: [2.2, 8.4, 1.0],
      rHand: [2.8, 13.4, -1.8],
      rElbow: [1.0, 11.0, -2.6],
    });
    k.box(0.22, 5.4, 0.1, 3.4, 16.0, -1.8, COL.STEEL, 0, 0, -0.3);
    k.box(1.0, 0.25, 0.4, 2.8, 13.5, -1.8, COL.BRONZE);
    k.bar([-1.4, 6.0, 2.3], [-0.2, 10.4, 2.6], 0.14, 0.14, COL.WOOD, 5);
    for (let y = 7.6; y <= 10.2; y += 0.65) k.box(0.12, 0.2, 0.2, 1.3, y, 0.45, COL.BRONZE);
  },

  machine_gunner(g, civ) {
    // A gunner kneeling behind a water-cooled gun on a tripod, behind sandbags.
    const crew = new T.Group();
    crew.position.set(-2.4, -3.0, 0);
    g.add(crew);
    const k = person(crew, civ, {
      helmet: 'kettle',
      tunic: KHAKI,
      legs: KHAKI,
      boots: COL.BLACK,
      skirt: false,
      lHand: [3.8, 10.2, 0.8],
      rHand: [3.0, 10.0, -0.6],
    });
    k.bar([0.15, 9.6, 1.5], [0.25, 10.2, 1.6], 0.5, 0.5, civ, 8);
    const gk = kit(g);
    for (const [a, b] of [[[2.4, 6.4, 0], [0.6, 0, 1.8]], [[2.4, 6.4, 0], [0.6, 0, -1.8]], [[2.4, 6.4, 0], [4.6, 0, 0]]] as Array<[[number, number, number], [number, number, number]]>)
      gk.bar(a, b, 0.12, 0.12, COL.IRON, 5);
    gk.box(2.6, 1.4, 1.0, 1.8, 7.0, 0, COL.IRON);
    gk.bar([2.8, 7.2, 0], [7.4, 7.2, 0], 0.55, 0.55, GREY_D, 10);
    gk.bar([7.4, 7.2, 0], [8.4, 7.2, 0], 0.16, 0.16, COL.IRON, 6);
    gk.box(0.8, 1.0, 0.3, 1.8, 6.4, 0.7, 0x5a5a40);
    gk.box(1.6, 0.8, 0.12, 1.8, 7.0, 0.62, civ);
    for (let i = 0; i < 7; i++) {
      const a = -0.9 + (i / 6) * 1.8;
      gk.ball(1.1, 5.6 + Math.cos(a) * 2.2, 0.8, Math.sin(a) * 4.0, 0xc4b48a, 1, 1.3, 0.6, 1.0);
      if (i % 2 === 0) gk.ball(1.1, 5.4 + Math.cos(a) * 2.2, 1.9, Math.sin(a) * 4.0, 0xb8a87e, 1, 1.3, 0.6, 1.0);
    }
  },

  mech_infantry(g, civ) {
    const k = kit(g);
    const hullColour = shadeOf(civ, 0.85);
    // An eight-wheeled armoured carrier with a small turret and rear doors.
    for (const x of [-4.6, -1.6, 1.6, 4.6]) for (const z of [2.6, -2.6]) {
      k.bar([x, 1.5, z - 0.5], [x, 1.5, z + 0.5], 1.5, 1.5, COL.BLACK, 12).rotation.set(Math.PI / 2, 0, 0);
      k.bar([x, 1.5, z * 1.21], [x, 1.5, z * 1.22], 0.7, 0.7, COL.IRON, 8);
    }
    k.shape([[-6.8, 1.6], [5.2, 1.6], [7.8, 3.4], [6.4, 5.2], [-6.6, 5.2], [-7.0, 4.6]], 4.6, hullColour, 0, 0, -2.3);
    k.box(13.0, 0.3, 5.0, -0.4, 3.0, 0, shadeOf(civ, 0.7));
    k.bar([0.4, 5.2, 0], [0.4, 6.6, 0], 1.3, 1.5, civ, 8);
    k.bar([1.4, 6.0, 0], [6.6, 6.2, 0], 0.16, 0.18, COL.IRON, 6);
    for (const z of [1.2, -1.2]) k.box(0.1, 2.2, 1.6, -6.85, 3.6, z, shadeOf(civ, 0.75));
    for (const [x, z] of [[3.6, 1.3], [3.6, -1.3]] as Array<[number, number]>) k.box(1.2, 0.2, 1.0, x, 5.3, z, COL.DARK);
    k.bar([-5.0, 5.2, -1.6], [-5.6, 10.4, -1.6], 0.04, 0.04, COL.BLACK, 3);
    for (const z of [2.32, -2.32]) k.disc(0.7, 5, COL.WHITE, -3.0, 4.0, z, z > 0 ? 0 : Math.PI);
  },

  artillery(g, civ) {
    const k = kit(g);
    // A field gun: shield, long barrel on its recoil cradle, split trail, two wheels.
    for (const z of [0.9, -0.9]) k.bar([1.4, 3.4, z * 0.4], [-8.0, 0.4, z * 1.8], 0.32, 0.32, shadeOf(civ, 0.9), 6);
    k.box(0.9, 0.6, 2.8, -8.0, 0.4, 0, COL.IRON);
    k.box(2.6, 1.2, 1.4, 1.2, 4.0, 0, GREY_D);
    k.bar([0.0, 4.6, 0], [10.4, 5.4, 0], 0.36, 0.32, COL.IRON, 10);
    k.bar([0.0, 4.0, 0], [5.6, 4.4, 0], 0.4, 0.4, GREY_D, 10);
    k.shape([[-2.8, 0], [2.8, 0], [2.8, 3.6], [1.2, 4.4], [-1.2, 4.4], [-2.8, 3.6]], 0.18, civ, 2.6, 2.6, 0, Math.PI / 2);
    for (const z of [2.4, -2.4]) k.wheel(1.2, 3.0, z, 2.9, COL.WOODD, 12, 0.32);
    k.bar([1.2, 3.0, 2.6], [1.2, 3.0, -2.6], 0.22, 0.22, COL.IRON, 8);
    for (const [x, z] of [[-4.6, 3.6], [-3.8, 3.6]] as Array<[number, number]>) k.bar([x, 0, z], [x, 1.6, z], 0.35, 0.35, COL.BRONZE, 8);
  },

  rocket_artillery(g, civ) {
    const k = kit(g);
    const body = shadeOf(civ, 0.85);
    // A six-wheeled truck: cab, chassis, and a raised rack of launch tubes.
    for (const x of [-4.4, -2.0, 4.4]) for (const z of [2.3, -2.3]) {
      k.bar([x, 1.4, z - 0.45], [x, 1.4, z + 0.45], 1.4, 1.4, COL.BLACK, 12).rotation.set(Math.PI / 2, 0, 0);
      k.bar([x, 1.4, z * 1.2], [x, 1.4, z * 1.21], 0.6, 0.6, COL.IRON, 8);
    }
    k.box(13.0, 0.8, 4.0, -0.4, 2.6, 0, COL.IRON);
    k.box(3.4, 3.4, 4.4, 4.4, 4.6, 0, body);
    k.box(0.2, 1.4, 3.6, 6.12, 5.4, 0, 0x9cc3d3);
    for (const z of [2.22, -2.22]) k.box(1.4, 1.2, 0.05, 4.6, 5.4, z, 0x9cc3d3);
    k.box(1.2, 0.2, 4.6, 6.2, 3.0, 0, COL.IRON);
    const rack = new T.Group();
    rack.position.set(-2.4, 4.4, 0);
    rack.rotation.z = 0.5;
    g.add(rack);
    const rk = kit(rack);
    for (let r = 0; r < 3; r++) for (let c = 0; c < 4; c++) rk.bar([-3.6, 0.6 + r * 0.9, -1.4 + c * 0.93], [4.2, 0.6 + r * 0.9, -1.4 + c * 0.93], 0.4, 0.4, GREY_D, 8);
    rk.box(8.0, 0.3, 4.2, 0.3, 0.1, 0, body);
    k.box(1.0, 2.4, 1.0, -2.0, 3.6, 0, COL.IRON);
    for (const z of [2.42, -2.42]) k.disc(0.6, 5, COL.WHITE, 4.4, 4.8, z, z > 0 ? 0 : Math.PI);
  },

  frigate(g, civ) {
    const k = kit(g);
    hull(k, 20, 5.0, 5.0, 0x2f3438, 2.0, 1.4);
    // Two rows of gunports on a buff band, three masts of square sails, the owner's ensign.
    for (const z of [2.52, -2.52]) {
      k.box(17, 1.2, 0.05, -0.6, 3.4, z, 0xd9c79a);
      for (let x = -7; x <= 6; x += 1.6) k.box(0.6, 0.6, 0.06, x, 3.4, z, COL.BLACK);
      k.box(17, 0.4, 0.06, -0.6, 4.7, z, civ);
    }
    for (const [x, h, w] of [[5.0, 18, 6.0], [-0.4, 21, 7.0], [-5.8, 17, 5.0]] as Array<[number, number, number]>) {
      k.bar([x, 5.0, 0], [x, h, 0], 0.24, 0.18, COL.WOODD, 6);
      squareSails(g, k, x, h, w);
    }
    k.bar([10.0, 6.4, 0], [14.4, 9.0, 0], 0.18, 0.12, COL.WOODD, 5);
    for (const z of [2.0, -2.0]) k.bar([-0.4, 20.6, 0], [-9.2, 6.0, z], 0.03, 0.03, COL.DARK, 3);
    k.bar([-9.6, 5.6, 0], [-11.0, 9.6, 0], 0.06, 0.06, COL.WOODD, 4);
    k.shape([[0, 0], [-2.6, 0], [-2.6, -1.6], [0, -1.6]], 0, civ, -11.0, 9.6, 0);
  },

  ironclad(g, civ) {
    const k = kit(g);
    steelHull(k, civ, 18, 5.0, 2.4, 0x4a5056);
    // A sloped iron casemate with gunports, one tall funnel, a flagstaff.
    k.shape([[-5.0, 0], [5.0, 0], [4.0, 2.8], [-4.0, 2.8]], 4.0, 0x3e4449, 0, 2.5, -2.0);
    for (const z of [2.0, -2.0]) for (const x of [-2.6, 0, 2.6]) k.box(0.8, 0.7, 0.08, x, 3.6, z * 0.98, COL.BLACK);
    k.box(8.0, 0.2, 4.0, 0, 5.4, 0, 0x5a6066);
    funnel(k, civ, -0.6, 5.4, 4.2, 0.75);
    k.bar([-8.4, 2.4, 0], [-8.4, 8.0, 0], 0.06, 0.06, COL.WOODD, 4);
    k.shape([[0, 0], [-2.2, 0], [-2.2, -1.4], [0, -1.4]], 0, civ, -8.4, 8.0, 0);
  },

  destroyer(g, civ) {
    const k = kit(g);
    steelHull(k, civ, 22, 3.6, 2.6);
    turret(g, k, 7.0, 2.8, 1.2, 1);
    turret(g, k, -7.6, 2.8, 1.2, 1, Math.PI);
    k.box(3.4, 2.4, 2.6, 3.6, 4.0, 0, GREY);
    k.box(2.2, 1.0, 2.4, 3.9, 5.6, 0, 0x9cc3d3);
    k.box(2.4, 0.3, 2.6, 3.9, 6.2, 0, GREY);
    funnel(k, civ, 0.6, 2.8, 3.4, 0.6);
    funnel(k, civ, -2.0, 2.8, 3.0, 0.6);
    k.bar([2.8, 6.2, 0], [2.6, 11.4, 0], 0.08, 0.08, GREY_D, 4);
    k.box(0.1, 0.1, 1.8, 2.7, 10.2, 0, GREY_D);
    k.box(2.0, 0.6, 0.6, -4.6, 3.2, 1.0, GREY_D);
    k.box(2.0, 0.6, 0.6, -4.6, 3.2, -1.0, GREY_D);
  },

  transport(g, civ) {
    const k = kit(g);
    steelHull(k, civ, 22, 5.2, 3.6, 0x3e4a52);
    // Holds with hatch covers and crates forward, derricks, a white bridge aft, one funnel.
    for (const x of [6.0, 2.0]) {
      k.box(3.0, 0.6, 3.6, x, 4.0, 0, 0x6b5a44);
      k.box(1.4, 1.0, 1.2, x, 4.8, 0.8, 0xb8975e);
      k.box(1.2, 0.9, 1.1, x - 0.6, 4.75, -0.9, civ);
    }
    for (const x of [4.0]) {
      k.bar([x, 3.6, 0], [x, 10.4, 0], 0.18, 0.15, COL.IRON, 6);
      k.bar([x, 4.6, 0], [x + 3.6, 8.4, 0], 0.1, 0.1, COL.IRON, 5);
      k.bar([x, 4.6, 0], [x - 3.4, 8.2, 0], 0.1, 0.1, COL.IRON, 5);
    }
    k.box(5.0, 3.0, 4.6, -6.0, 5.2, 0, COL.WHITE);
    k.box(4.0, 1.4, 4.2, -5.6, 7.4, 0, COL.WHITE);
    k.box(4.1, 0.5, 4.3, -5.6, 7.6, 0, 0x5a6b78);
    funnel(k, civ, -8.4, 6.6, 3.2, 0.9);
    for (const z of [2.64, -2.64]) for (let x = -7.6; x <= -4.6; x += 1.0) k.box(0.4, 0.4, 0.04, x, 5.2, z * 0.88, 0x5a6b78);
  },

  cruiser(g, civ) {
    const k = kit(g);
    steelHull(k, civ, 26, 4.8, 3.0);
    turret(g, k, 8.6, 3.2, 1.5, 2);
    turret(g, k, 5.2, 4.4, 1.4, 2);
    k.box(2.0, 1.2, 2.0, 5.2, 3.8, 0, GREY);
    turret(g, k, -9.0, 3.2, 1.5, 2, Math.PI);
    k.box(4.4, 3.0, 3.6, 1.8, 4.6, 0, GREY);
    k.box(3.0, 1.2, 3.2, 2.2, 6.6, 0, 0x9cc3d3);
    k.box(3.2, 0.3, 3.4, 2.2, 7.3, 0, GREY);
    funnel(k, civ, -1.6, 3.2, 4.0, 0.8);
    funnel(k, civ, -4.0, 3.2, 3.6, 0.8);
    k.bar([1.6, 7.3, 0], [1.4, 13.6, 0], 0.1, 0.1, GREY_D, 4);
    k.box(0.1, 0.1, 2.4, 1.5, 12.0, 0, GREY_D);
  },

  battleship(g, civ) {
    const k = kit(g);
    steelHull(k, civ, 30, 6.0, 3.4);
    turret(g, k, 10.4, 3.6, 2.0, 3);
    turret(g, k, 6.4, 5.0, 1.9, 3);
    k.box(2.6, 1.4, 2.6, 6.4, 4.3, 0, GREY);
    turret(g, k, -10.6, 3.6, 2.0, 3, Math.PI);
    // A tall stepped superstructure ("pagoda" mast), funnels, secondary guns.
    k.box(6.0, 3.2, 4.4, 1.6, 5.0, 0, GREY);
    k.box(4.0, 2.4, 3.6, 2.2, 7.8, 0, GREY);
    k.box(2.8, 2.2, 3.0, 2.6, 10.1, 0, GREY);
    k.box(2.6, 0.8, 2.8, 2.7, 11.6, 0, 0x9cc3d3);
    k.box(1.8, 1.6, 1.8, 2.8, 12.8, 0, GREY_D);
    k.bar([2.8, 13.6, 0], [2.7, 17.0, 0], 0.1, 0.1, GREY_D, 4);
    funnel(k, civ, -2.4, 3.4, 5.0, 1.1);
    for (const z of [2.6, -2.6]) for (const x of [0, -3.6, -6.0]) {
      k.box(1.0, 0.7, 0.9, x, 4.0, z * 0.7, GREY_D);
      k.bar([x + 0.4, 4.1, z * 0.7], [x + 2.2, 4.2, z * 0.8], 0.08, 0.08, GREY_D, 4);
    }
    k.bar([-13.6, 3.4, 0], [-13.6, 7.4, 0], 0.06, 0.06, GREY_D, 4);
    k.shape([[0, 0], [-2.4, 0], [-2.4, -1.5], [0, -1.5]], 0, civ, -13.6, 7.4, 0);
  },

  carrier(g, civ) {
    const k = kit(g);
    steelHull(k, civ, 30, 6.0, 3.6);
    // The flight deck overhanging the hull, its markings, the island, and two aircraft.
    const deck = new T.Mesh(new T.BoxGeometry(31, 0.4, 8.4), mat(0x5a6066));
    deck.position.set(0.2, 4.4, 0);
    g.add(deck);
    k.box(28, 0.05, 0.25, 0, 4.62, 0, COL.WHITE);
    for (let x = -13; x <= 12; x += 3) k.box(1.4, 0.05, 0.2, x, 4.62, 2.6, COL.WHITE);
    k.box(2.6, 0.05, 2.6, -11.0, 4.62, 0, civ);
    k.box(4.0, 3.6, 1.8, 2.0, 6.4, -3.4, GREY);
    k.box(3.0, 1.0, 1.6, 2.4, 8.6, -3.4, 0x9cc3d3);
    funnel(k, civ, 0.6, 8.2, 2.2, 0.7);
    k.bar([3.0, 9.2, -3.4], [2.8, 13.0, -3.4], 0.08, 0.08, GREY_D, 4);
    for (const [x, z] of [[-6.0, 1.6], [-9.6, -1.4]] as Array<[number, number]>) {
      const plane = new T.Group();
      plane.position.set(x, 4.2, z);
      plane.scale.setScalar(0.32);
      plane.rotation.y = 0.2;
      g.add(plane);
      aircraft(plane, civ, 11, 8.5, 1, false, 1.4);
    }
  },

  fighter(g, civ) {
    aircraft(g, civ, 11, 8.5, 1, false);
  },

  jet_fighter(g, civ) {
    const k = aircraft(g, civ, 9, 11, 0, true);
    k.bar([-5.6, 5.2, 0], [-6.4, 5.2, 0], 0.55, 0.45, COL.IRON, 10);
  },

  bomber(g, civ) {
    aircraft(g, civ, 22, 14, 4, false, 6.0);
  },
};

/** Two tiers of square sails on a mast. */
function squareSails(g: T.Group, k: Kit, x: number, h: number, w: number): void {
  for (const [top, height, width] of [[h - 0.8, 4.6, w], [h - 6.0, 5.4, w * 1.15]] as Array<[number, number, number]>) {
    k.bar([x, top + 0.2, -width / 2 - 0.3], [x, top + 0.2, width / 2 + 0.3], 0.12, 0.12, COL.WOODD, 5);
    const sail = new T.Mesh(new T.CylinderGeometry(width * 1.4, width * 1.4, height, 10, 1, true, -0.38, 0.76), mat(COL.WHITE, true));
    sail.rotation.y = Math.PI / 2;
    sail.position.set(x - width * 1.4 + 0.6, top - height / 2, 0);
    g.add(sail);
  }
}
